import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GoogleAuthButton from '../../components/GoogleAuthButton';
import MobileOnlyGate from '../../components/MobileOnlyGate';
import googleAnalytics from '../../services/googleAnalytics';
import '../../styles/logo-animation.css';
import investingLogo from '../../assets/images/mainlogo.png';
import { useIsMobile } from '../../hooks/useIsMobile';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [errorType, setErrorType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLogoAnimation, setShowLogoAnimation] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Hide the logo animation after 2 seconds
    const timer = setTimeout(() => {
      setShowLogoAnimation(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);


  // Show mobile-only gate on desktop
  if (!isMobile) {
    return <MobileOnlyGate type="login" />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear error type when user starts typing
    if (errorType) {
      setErrorType(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.usernameOrEmail) newErrors.usernameOrEmail = 'Username or email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      // Backend always expects 'username' field, regardless of whether it's email or username
      const loginData = {
        username: formData.usernameOrEmail,
        password: formData.password
      };
      
      console.log('Attempting login with:', loginData);
      const result = await login(loginData);
      console.log('Login result:', result);
      
      if (result.success) {
        // Track successful login
        googleAnalytics.trackAuth.login('email');
        
        console.log('Login successful, navigating to dashboard...');
        navigate('/dashboard');
      } else {
        const errorMessage = result.error || 'Login failed. Please try again.';
        const errorType = result.errorType || 'GENERIC_ERROR';
        console.log('Login failed:', errorMessage, 'Type:', errorType);
        setErrors({ general: errorMessage });
        setErrorType(errorType);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.userMessage || error.message || 'An unexpected error occurred. Please try again.';
      setErrors({ general: errorMessage });
      setErrorType(error.errorType || 'GENERIC_ERROR');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Logo Popup Animation */}
      {showLogoAnimation && (
        <div className="logo-popup-overlay">
          <div className="logo-popup-container">
            <img 
              src={investingLogo} 
              alt="Kids Investment Game" 
              className="logo-popup-image"
            />
          </div>
        </div>
      )}

      <div style={{
      minHeight: '100vh',
      background: '#000000',
      color: '#FFFFFF',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      position: 'relative'
    }}>

      {/* Main Login Form */}
      <div style={{
        maxWidth: '440px',
        width: '100%',
        background: '#FFFFFF',
        border: '2px solid #000000',
        borderRadius: '0',
        boxShadow: '6px 6px 0px #000000',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          background: '#000000',
          color: '#FFFFFF',
          padding: '1.5rem 2rem',
          textAlign: 'center'
        }}>
          <img
            src={investingLogo}
            alt="Gideon the Bull"
            style={{
              display: 'inline-block',
              width: '84px',
              height: '84px',
              objectFit: 'contain',
              background: '#FFFFFF',
              border: '3px solid #000000',
              boxShadow: '4px 4px 0px #333333',
              padding: '6px',
              marginBottom: '0.75rem'
            }}
          />
          <h1 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1.75rem',
            fontWeight: '800',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>Welcome Back</h1>
          <p style={{
            margin: '0',
            fontSize: '0.9rem',
            fontWeight: '500',
            opacity: '0.85'
          }}>Ready to continue your investment journey?</p>
        </div>

        {/* Account Not Found Error Message */}
        {errorType === 'ACCOUNT_NOT_FOUND' && (
          <div style={{
            margin: '16px',
            padding: '16px',
            background: '#D72638',
            color: '#FFFFFF',
            textAlign: 'center'
          }}>
            <p style={{
              margin: '0 0 12px 0',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              No account found with these credentials.
            </p>
            <Link to="/signup" style={{
              display: 'inline-block',
              padding: '10px 20px',
              background: '#FFFFFF',
              color: '#D72638',
              border: 'none',
              borderRadius: '0',
              fontSize: '14px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              textDecoration: 'none',
              transition: 'all 0.2s ease'
            }}>
              Create Account
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          padding: '1.5rem',
          background: '#FFFFFF'
        }}>
          {errors.general && (
            <div style={{
              background: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FECACA',
              padding: '12px 16px',
              marginBottom: '16px',
              borderRadius: '0',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {errors.general}
            </div>
          )}

          <div style={{ marginBottom: '1.25rem' }}>
            <label htmlFor="usernameOrEmail" style={{
              display: 'block',
              marginBottom: '6px',
              fontWeight: '800',
              color: '#000000',
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>Username or Email</label>
            <input
              type="text"
              id="usernameOrEmail"
              name="usernameOrEmail"
              value={formData.usernameOrEmail}
              onChange={handleChange}
              placeholder="Enter your username or email"
              style={{
                width: '100%',
                padding: '14px 16px',
                background: '#FFFFFF',
                color: '#000000',
                border: errors.usernameOrEmail ? '2px solid #D72638' : '2px solid #000000',
                borderRadius: '0',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '3px 3px 0px rgba(0,0,0,0.1)',
                outline: 'none',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
              }}
            />
            {errors.usernameOrEmail && (
              <span style={{
                color: '#D72638',
                fontSize: '0.75rem',
                fontWeight: '600',
                marginTop: '4px',
                display: 'block'
              }}>{errors.usernameOrEmail}</span>
            )}
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label htmlFor="password" style={{
              display: 'block',
              marginBottom: '6px',
              fontWeight: '800',
              color: '#000000',
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  paddingRight: '50px',
                  background: '#FFFFFF',
                  color: '#000000',
                  border: errors.password ? '2px solid #D72638' : '2px solid #000000',
                  borderRadius: '0',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '3px 3px 0px rgba(0,0,0,0.1)',
                  outline: 'none',
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  color: '#999999',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.password && (
              <span style={{
                color: '#D72638',
                fontSize: '0.75rem',
                fontWeight: '600',
                marginTop: '4px',
                display: 'block'
              }}>{errors.password}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: '#000000',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '0',
              fontSize: '15px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '4px 4px 0px #000000',
              transition: 'all 0.2s ease',
              opacity: loading ? 0.7 : 1,
              marginTop: '0.5rem'
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        {/* Divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          margin: '0.75rem 1.5rem 1rem 1.5rem'
        }}>
          <div style={{
            flex: 1,
            height: '1px',
            background: '#E5E5E5'
          }}></div>
          <span style={{
            padding: '0 1rem',
            color: '#666666',
            fontWeight: '600',
            fontSize: '12px',
            textTransform: 'uppercase'
          }}>or</span>
          <div style={{
            flex: 1,
            height: '1px',
            background: '#E5E5E5'
          }}></div>
        </div>

        {/* Google Sign In Button */}
        <div style={{ padding: '0 1.5rem' }}>
          <GoogleAuthButton
            mode="signin"
            buttonText="Continue with Google"
            onSuccess={(user) => {
              console.log('Google Sign In successful:', user);
            }}
            onError={(error) => {
              console.error('Google Sign In error:', error);
              setErrors({ general: 'Google Sign In failed. Please try again.' });
            }}
          />
        </div>

        <div style={{
          textAlign: 'center',
          padding: '1.25rem 1.5rem',
          background: '#F5F5F5',
          borderTop: '1px solid #E5E5E5',
          marginTop: '1rem'
        }}>
          <p style={{
            margin: '0',
            fontSize: '0.9rem',
            color: '#666666'
          }}>
            New to investing?{' '}
            <Link to="/signup" style={{
              color: '#000000',
              fontWeight: '700',
              textDecoration: 'none'
            }}>
              Create Account
            </Link>
          </p>
        </div>
      </div>

    </div>
    </>
  );
};

export default LoginPage;